import { ArrowDownTrayIcon, CheckIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
// import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import Button from "src/components/Shared/Buttons/Button";
// import TextArea from "src/components/Shared/Forms/Inputs/TextArea";
import Preloader from "src/components/Shared/LoadingAnimations/Preloader";
import SlideOver from "src/components/Shared/SlideOver";
import Badge from "src/components/ServerMonitoring/Badge";
import { classNames } from "src/helpers/classNames";
import { confidenceStyles, formatBytes, formatDateTime, formatDuration, formatPercent, incidentStatusStyles, stripAnsi } from "src/helpers/serverMonitorHelper";

const DetailRow = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:gap-4">
    <dt className="w-48 flex-shrink-0 text-sm font-medium text-gray-500">{label}</dt>
    <dd className="break-words text-sm text-gray-900">{children ?? "—"}</dd>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div className="border-t border-gray-100 py-4 first:border-t-0 first:pt-0">
    <h4 className="mb-2 text-base font-semibold text-gray-900">{title}</h4>
    <dl className="divide-y divide-gray-50">{children}</dl>
  </div>
);

const downloadLog = (content, incident, index) => {
  const stamp = formatDateTime(incident?.occurred_at || incident?.detected_at, "YYYY-MM-DD_HH-mm-ss");
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `crash-log_${incident?.component_name || "server"}_${stamp}${index ? `_${index + 1}` : ""}.txt`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// Copy button that confirms in place (icon pops to a check, button turns green) instead of showing a toast.
const CopyLogButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onCopy = () => {
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CopyToClipboard
      text={text}
      onCopy={onCopy}>
      <Button
        type="button"
        version="default"
        className={classNames("flex !h-8 items-center gap-1 rounded border px-2 shadow-sm transition-colors duration-200", copied ? "border-green-300 bg-green-50 text-green-700" : "border-gray-200 bg-white text-gray-500 hover:text-gray-700")}>
        <span className="relative flex h-4 w-4 items-center justify-center">
          <AnimatePresence
            initial={false}
            exitBeforeEnter>
            {copied ? (
              <motion.span
                key="copied"
                initial={{ scale: 0, rotate: -45, opacity: 0 }}
                animate={{ scale: [0, 1.35, 1], rotate: 0, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.35 }}>
                <CheckIcon className="h-4 w-4 stroke-[2.5]" />
              </motion.span>
            ) : (
              <motion.span
                key="copy"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}>
                <DocumentDuplicateIcon className="h-4 w-4" />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
        <span
          className="inline-block w-12 text-left"
          aria-live="polite">
          {copied ? "Copied" : "Copy"}
        </span>
      </Button>
    </CopyToClipboard>
  );
};

const CrashLogViewer = ({ log, incident, index }) => {
  const content = stripAnsi(log?.content);
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-gray-700">{log?.source}</span>
          <span>Retrieved {formatDateTime(log?.retrieved_at)}</span>
          <span>{formatBytes(log?.original_bytes)}</span>
          {log?.truncated && (
            <Badge
              label="Truncated"
              className="bg-amber-100 text-amber-700"
            />
          )}
          {log?.possibly_previous_crash && (
            <Badge
              label="Possibly from an earlier crash"
              className="bg-gray-100 text-gray-600"
            />
          )}
        </div>
        <div className="flex gap-2">
          <CopyLogButton text={content} />
          <Button
            type="button"
            version="default"
            onClick={() => downloadLog(content, incident, index)}
            className="flex !h-8 items-center gap-1 rounded border border-gray-200 bg-white px-2 text-gray-500 shadow-sm hover:text-gray-700">
            <ArrowDownTrayIcon className="h-4 w-4" /> Download
          </Button>
        </div>
      </div>
      <pre className="max-h-[420px] overflow-auto whitespace-pre rounded-md bg-gray-900 p-4 font-mono text-xs leading-5 text-gray-100">{content || "(empty log)"}</pre>
    </div>
  );
};

const CrashIncidentDetailModal = ({ incidentId, onClose, onChanged = () => {} }) => {
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // const [note, setNote] = useState("");
  // const [savingNote, setSavingNote] = useState(false);
  // const [resolveOpen, setResolveOpen] = useState(false);
  // const [resolveNote, setResolveNote] = useState("");
  // const [resolving, setResolving] = useState(false);

  // const reload = useCallback(async () => {
  //   const res = await apiRequest("get", `/server-monitor/incidents/:incident_id`, { params: { incident_id: incidentId } });
  //   setIncident(res.data.data);
  // }, [incidentId]);

  useEffect(() => {
    if (!incidentId) return;
    let active = true;
    // setNote("");
    // setResolveOpen(false);
    // setResolveNote("");
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setIncident(null);
        const res = await apiRequest("get", `/server-monitor/incidents/:incident_id`, { params: { incident_id: incidentId } });
        if (active) setIncident(res.data.data);
      } catch (err) {
        if (active) setError(err.message || "Unable to load crash details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [incidentId]);

  // const addNote = async () => {
  //   try {
  //     setSavingNote(true);
  //     await apiRequest("post", `/server-monitor/incidents/:incident_id/notes`, { params: { incident_id: incidentId }, body: { note } });
  //     setNote("");
  //     await reload();
  //   } catch (err) {
  //     toast.error(err.message);
  //   } finally {
  //     setSavingNote(false);
  //   }
  // };

  // const resolve = async () => {
  //   try {
  //     setResolving(true);
  //     const res = await apiRequest("put", `/server-monitor/incidents/:incident_id/resolve`, { params: { incident_id: incidentId }, body: { note: resolveNote } });
  //     toast.success(res.data.message || "Incident marked as resolved.");
  //     setResolveOpen(false);
  //     setResolveNote("");
  //     await reload();
  //     onChanged();
  //   } catch (err) {
  //     toast.error(err.message);
  //   } finally {
  //     setResolving(false);
  //   }
  // };

  const status = incidentStatusStyles[incident?.status] || incidentStatusStyles.crashed;
  const confidence = confidenceStyles[incident?.reason_confidence] || confidenceStyles.unknown;

  return (
    <SlideOver
      title="Crash Details"
      description={incident ? formatDateTime(incident.occurred_at || incident.detected_at, "MMM D, YYYY h:mm A") : ""}
      isOpen={!!incidentId}
      size="4xl"
      handleClose={onClose}
      bodyClassName="!p-6">
      {loading && (
        <div className="flex h-40 items-center justify-center">
          <Preloader />
        </div>
      )}
      {error && <p className="py-6 text-sm text-red-600">{error}</p>}
      {incident && !loading && (
        <div className="pb-4">
          <DetailSection title="Crash Information">
            <DetailRow label="Status">
              <Badge
                label={status.label}
                className={status.badge}
              />
            </DetailRow>
            <DetailRow label="Crash Time">{formatDateTime(incident.occurred_at)}</DetailRow>
            <DetailRow label="Detected At">{formatDateTime(incident.detected_at)}</DetailRow>
            <DetailRow label="Recovered At">{incident.recovered_at ? formatDateTime(incident.recovered_at) : "Not yet recovered"}</DetailRow>
            {incident.resolution?.kind && (
              <DetailRow label="Closed">
                {incident.resolution.kind === "manual" ? (
                  <>
                    Manually by <b>{incident.resolution.by_name}</b> on {formatDateTime(incident.resolution.at)} - {incident.resolution.note}
                  </>
                ) : (
                  "Automatically - the Node server was running and healthy again"
                )}
              </DetailRow>
            )}
            <DetailRow label="Downtime">
              {incident.status === "recovered" ? formatDuration(incident.downtime_seconds, incident.downtime_is_upper_bound) : "Ongoing"}
              {incident.downtime_is_upper_bound && <span className="ml-2 text-xs text-gray-400">(restart completed between health checks - actual downtime was shorter)</span>}
            </DetailRow>
            <DetailRow label="Live Version">
              {incident.live_deployment ? (
                <>
                  {incident.live_deployment.triggered_by?.commit ? (
                    <>
                      {incident.live_deployment.triggered_by.commit.url ? (
                        <a
                          href={incident.live_deployment.triggered_by.commit.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-highlightColor hover:underline">
                          {incident.live_deployment.triggered_by.commit.sha}
                        </a>
                      ) : (
                        <span className="font-mono">{incident.live_deployment.triggered_by.commit.sha}</span>
                      )}{" "}
                      {incident.live_deployment.triggered_by.commit.message}
                    </>
                  ) : (
                    incident.live_deployment.cause || incident.live_deployment.id
                  )}
                  {incident.live_deployment.triggered_by?.name && <span className="text-gray-500"> by {incident.live_deployment.triggered_by.name}</span>}
                  {incident.live_deployment.live_at && (
                    <span className="block text-xs text-gray-500">
                      Live since {formatDateTime(incident.live_deployment.live_at)} - {formatDuration(Math.max(0, (new Date(incident.occurred_at || incident.detected_at) - new Date(incident.live_deployment.live_at)) / 1000))} before the crash
                    </span>
                  )}
                </>
              ) : (
                "Unknown"
              )}
            </DetailRow>
            <DetailRow label="DigitalOcean State">{incident.digital_ocean_state}</DetailRow>
            <DetailRow label="Component">{incident.component_name}</DetailRow>
            <DetailRow label="Detected From">
              {{ unhealthy: "Component health became UNHEALTHY", process_crash: "Crash message in the runtime (RUN) log - the Node process stopped while the container kept running" }[incident.evidence_type] || "DigitalOcean restart_count metric"}
            </DetailRow>
            <DetailRow label="Restart Information">
              {incident.restart?.detected ? (
                <>
                  Yes - {incident.restart.count} restart{incident.restart.count === 1 ? "" : "s"}, first at {formatDateTime(incident.restart.metric_timestamp)}
                  {incident.restart.events?.[0]?.instance && <span className="text-gray-400"> (instance {incident.restart.events[0].instance})</span>}
                </>
              ) : (
                "No restart reported by the restart metric"
              )}
            </DetailRow>
            <DetailRow label="Replicas at Detection">{incident.replicas?.ready != null || incident.replicas?.desired != null ? `${incident.replicas?.ready ?? "—"} / ${incident.replicas?.desired ?? "—"}` : "—"}</DetailRow>
          </DetailSection>

          {/* "Mark as resolved" - commented out
          {incident.status === "crashed" && (
            <div className="mb-4 rounded-md border border-gray-200 px-4 py-3">
              {!resolveOpen ? (
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                  <span>This incident closes automatically when the Node server is running again. Close it manually only if it cannot close itself.</span>
                  <Button
                    version="secondary"
                    type="button"
                    onClick={() => setResolveOpen(true)}>
                    <CheckCircleIcon className="h-4 w-4" /> Mark as resolved
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <TextArea
                    label="Why is it resolved? (required)"
                    value={resolveNote}
                    placeholder="e.g. Component was renamed - the old incident cannot recover by itself."
                    onChange={(e) => setResolveNote(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      version="gray"
                      type="button"
                      onClick={() => setResolveOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      version="primary"
                      type="button"
                      disabled={!resolveNote.trim() || resolving}
                      loading={resolving}
                      onClick={resolve}>
                      Mark as resolved
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          */}

          <DetailSection title="Detected Reason">
            <DetailRow label="Reason">
              <span className="font-semibold">{incident.reason}</span>
            </DetailRow>
            <DetailRow label="Confidence">
              <Badge
                label={confidence.label}
                className={confidence.badge}
              />
            </DetailRow>
            {incident.reason_excerpt && (
              <DetailRow label="Matched Log Lines">
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 font-mono text-xs text-gray-700">{stripAnsi(incident.reason_excerpt)}</pre>
              </DetailRow>
            )}
            {incident.supporting_signals?.length > 0 && (
              <DetailRow label="Supporting Signals">
                <ul className="list-disc space-y-1 pl-4">
                  {incident.supporting_signals.map((signal) => (
                    <li key={signal}>{signal}</li>
                  ))}
                </ul>
              </DetailRow>
            )}
          </DetailSection>

          <DetailSection title="Resource Usage">
            <DetailRow label="Memory Before Crash">{formatPercent(incident.memory?.last_before_crash)}</DetailRow>
            <DetailRow label="Maximum Memory">{formatPercent(incident.memory?.max_before_crash)}</DetailRow>
            <DetailRow label="CPU Before Crash">{formatPercent(incident.cpu?.last_before_crash)}</DetailRow>
            <DetailRow label="Maximum CPU">{formatPercent(incident.cpu?.max_before_crash)}</DetailRow>
            <DetailRow label="Metric Window">
              {incident.memory?.window_start ? `${formatDateTime(incident.memory.window_start, "h:mm:ss A")} – ${formatDateTime(incident.memory.window_end, "h:mm:ss A")} (${incident.memory?.samples?.length || 0} memory samples)` : "—"}
              {(incident.memory?.error || incident.cpu?.error) && <div className="text-xs text-amber-700">{incident.memory?.error || incident.cpu?.error}</div>}
            </DetailRow>
          </DetailSection>

          <div className="border-t border-gray-100 pt-4">
            <h4 className="mb-2 text-base font-semibold text-gray-900">DigitalOcean Crash Log</h4>
            {incident.crash_logs?.length > 0 ? (
              incident.crash_logs.map((log, index) => (
                <CrashLogViewer
                  key={`${log.content_hash}-${index}`}
                  log={log}
                  incident={incident}
                  index={index}
                />
              ))
            ) : (
              <p className="rounded-md bg-gray-50 px-4 py-6 text-sm text-gray-500">
                {incident.logs_pending ? "DigitalOcean has not made the RUN_RESTARTED log available yet. The Super Portal will keep trying for a few minutes." : "No RUN_RESTARTED log was available from DigitalOcean for this incident."}
                {incident.logs_error && <span className="mt-1 block text-xs text-amber-700">{incident.logs_error}</span>}
              </p>
            )}
          </div>

          {/* Notes list and "Add a note" - commented out
          <div className="mt-4 border-t border-gray-100 pt-4">
            <h4 className="mb-2 text-base font-semibold text-gray-900">Notes</h4>
            {incident.notes?.length > 0 ? (
              <ul className="mb-3 space-y-2">
                {incident.notes.map((item) => (
                  <li
                    key={item._id}
                    className="rounded-md bg-gray-50 px-3 py-2 text-sm">
                    <p className="whitespace-pre-wrap break-words text-gray-800">{item.text}</p>
                    <p className="mt-1 text-xs text-gray-400">
                      {item.created_by_name} · {formatDateTime(item.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-3 text-sm text-gray-400">No notes yet.</p>
            )}
            <TextArea
              label="Add a note"
              value={note}
              placeholder="e.g. Stray comma in api/server.js - fixed in commit b63dcde."
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="mt-2 flex justify-end">
              <Button
                version="secondary"
                type="button"
                disabled={!note.trim() || savingNote}
                loading={savingNote}
                onClick={addNote}>
                Add note
              </Button>
            </div>
          </div>
          */}
        </div>
      )}
    </SlideOver>
  );
};

export default CrashIncidentDetailModal;
